import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { messageHandler } from "utils/messageHandler";
import { BROKERAGE_FIRM_MESSAGES } from "utils/messages";
import { Row } from "@tanstack/react-table";
import { BrokerageFirm } from "types";
import DataTable from "components/DataTable";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import AddressAutocomplete from "components/AddressAutocomplete";
import Address_Details from "assets/images/Agents/Address_Details.png";
import BrokerageFirmHelpModal from "components/SignageBrokerageFirmToolTip";

import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
import { getImageSrc, usePresignedMap } from "components/Tableimageshelpers";
import imageCustomSquare from "assets/images/imagecustomrounded.png";
import {
  ExportAsExcel,
} from "@siamf/react-export";
import { showError } from "toast/toast";

interface ExportRow {
  "Company Name": string;
  "First Name": string;
  "Last Name": string;
  "Phone": string;
  "Email": string;
  "License Status": string;
  "Company Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<BrokerageFirm>();

const SignageBrokerageFirms = () => {
  const [brokerageFirms, setBrokerageFirms] = useState<BrokerageFirm[]>([]);
  const [filteredFirms, setFilteredFirms] = useState<BrokerageFirm[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const navigate = useNavigate();
  const [toastShown, setToastShown] = useState(false);
  const location = useLocation();

  // State for parent signage firm (for signageteammember login)
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.brokerage_firms
  );
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const fetchRequestIdRef = useRef(0);
  const hasLoadedDataRef = useRef(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteTargetName, setDeleteTargetName] = useState("");
     const [openHelp, setOpenHelp] = useState(false);    
  // Fetch parent hierarchy for signageteammember login
  const fetchParentHierarchy = async () => {
    if (!isSignageTeammember || !token) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Find the parent with license_type "SIGNAGE_FIRM"
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );
        
        if (signageFirm) {
          setParentSignageId(signageFirm.id || signageFirm._id);
          setParentSignageName(signageFirm.name);
        }
      }
    } catch (error) {
      console.error("Error fetching parent hierarchy:", error);
    }
  };

  useEffect(() => {
    if (isSignageTeammember && !parentSignageId) return;
    if (!token || !userId) return;

    const fetchBrokerageFirms = async () => {
      const requestId = ++fetchRequestIdRef.current;
      const showInitialLoader = !hasLoadedDataRef.current;
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users-search/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {
                // userType: "brokerage_firm",
                // status: "Active",
                // LicenseActive: "Expired",
                licenseType:"BROKERAGE_FIRM",
                signageFirmID: (isSignageTeammember && parentSignageId ? parentSignageId : userId),
                // linked_suppliers: "BROKERAGE_FIRM",
                // "created_by.id": userId,
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
              fields: ["id", "firmDetails", "firstName", "lastName", "email", "LicenseActive", "userType", "status", "created_at", "profilePic", "preview_url"],
            }),

          }
        );
        const result: any = await response.json();
        if (requestId !== fetchRequestIdRef.current) return;
        if (!response.ok) showError("Access denied. You don’t have view permission for Brokerage Firms.");
        if (!response.ok || result?.error) {
          messageHandler.brokerageFirm.error("fetch", new Error(BROKERAGE_FIRM_MESSAGES.fetch.error));
          throw new Error(BROKERAGE_FIRM_MESSAGES.fetch.error);
        }
        const firms = result.data ?? []; // Adjust based on actual structure
        setBrokerageFirms(firms);
        setFilteredFirms(firms);
        setRowCount(result?.total);
        hasLoadedDataRef.current = true;

      } catch (err) {
        if (requestId !== fetchRequestIdRef.current) return;
        console.error("Failed to load brokerage firms.");
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchBrokerageFirms();
  }, [
    token,
    userId,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    deleting,
    isSignageTeammember,
    parentSignageId,
  ]);

  // Fetch parent hierarchy for signageteammember login
  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isSignageTeammember, token]);

  const confirmDeleteFirm = async () => {
    if (!deleteTargetId) return;
    setDeleting(deleteTargetId);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${deleteTargetId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const result: any = await response.json();
      if (!response.ok || result?.error) {
        messageHandler.brokerageFirm.error("delete", new Error(BROKERAGE_FIRM_MESSAGES.delete.error));
        throw new Error(BROKERAGE_FIRM_MESSAGES.delete.error);
      }
      messageHandler.brokerageFirm.success("delete");
      setBrokerageFirms((prev) => prev?.filter((f) => f._id !== deleteTargetId));
    } catch {
      console.error("Failed to delete the firm.");
    } finally {
      setDeleting(null);
      setOpenDelete(false);
      setDeleteTargetId(null);
      setDeleteTargetName("");
    }
  };

  const handleDeleteClick = (firm: BrokerageFirm) => {
    setDeleteTargetId(firm._id);
    setDeleteTargetName(firm?.firmDetails?.companyName || "Firm");
    setOpenDelete(true);
  };

  const handleProButton = async (firmId: string, activate: boolean) => {
    const endpoint = activate
      ? `${process.env.REACT_APP_API_URL}/brokerage-License/${firmId}/activate`
      : `${process.env.REACT_APP_API_URL}/brokerage-License/${firmId}/deactivate`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const result: any = await response.json();
      if (!response.ok || result?.error) {
        if (activate) {
          messageHandler.brokerageFirm.activateLicense.error(new Error(BROKERAGE_FIRM_MESSAGES.activateLicense.error));
        } else {
          messageHandler.brokerageFirm.deactivateLicense.error(new Error(BROKERAGE_FIRM_MESSAGES.deactivateLicense.error));
        }
        throw new Error(activate ? BROKERAGE_FIRM_MESSAGES.activateLicense.error : BROKERAGE_FIRM_MESSAGES.deactivateLicense.error);
      } else {
        setBrokerageFirms(prevFirms =>
          prevFirms?.map(firm =>
            firm._id === firmId ? { ...firm, LicenseActive: activate } : firm
          )
        );
        if (activate) {
          messageHandler.brokerageFirm.activateLicense.success();
        } else {
          messageHandler.brokerageFirm.deactivateLicense.success();
        }
        // navigate(0);
      }
    } catch (err) {
      console.error("An error occurred while updating license.");
    }
  };

  function formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US"); // Outputs in MM/DD/YYYY format
  }

  const brokerageImageUrlMap = usePresignedMap(brokerageFirms as any[], "profilePic", token);

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => <div className="th-text S-No">S.No.</div>,
        cell: (info) => (
          <div className="text-sm S-No">
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),

      columnHelper.display({
        id: "image",
        header: () => <div className="th-text S-No-1">Image</div>,
        cell: (info) => {
          const firm = info.row.original as BrokerageFirm & { profilePic?: string; preview_url?: string };
          const raw = firm.profilePic;
          const src = firm.preview_url || getImageSrc(raw, brokerageImageUrlMap);
          const isId = !!raw && /^[0-9a-f]{24}$/i.test(raw);
          const imgStyle = { width: 50, height: 50, objectFit: "contain" as const, borderRadius: 4 };

          return (
            <div className="text-sm S-No-1">
              {src ? (
                <img
                  src={src}
                  alt="Brokerage"
                  style={imgStyle}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = imageCustomSquare;
                  }}
                />
              ) : raw && isId ? (
                <span>Loading…</span>
              ) : (
                <img src={imageCustomSquare} alt="No image" style={imgStyle} />
              )}
            </div>
          );
        },
      }),

      columnHelper.accessor((row: BrokerageFirm) => row.firmDetails?.companyName || "", {
        id: "companyName",
        header: () => <div className="th-text S-No-2">Company Name</div>,
        cell: (info) => <div className="text-sm S-No-2">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BrokerageFirm) => row.firstName, {
        id: "FirstName",
        header: () => <div className="th-text">First Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BrokerageFirm) => row.lastName, {
        id: "LastName",
        header: () => <div className="th-text">Last Name</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BrokerageFirm) => row.email, {
        id: "Email",
        header: () => <div className="th-text">Email</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BrokerageFirm) => row.agentCount, {
        id: "agentCount",
        header: () => <div className="th-text">Number Of Agents</div>,
        cell: (info) => <div className="text-sm ">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      columnHelper.accessor((row: BrokerageFirm) => formatDate(row.created_at), {
        id: "DateCreated",
        header: () => <div className="th-text">Date Created</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }),

      // Only show Activate License column for signage login (not signageteammember)
      // ...(isSignageTeammember ? [] : [
      //   columnHelper.display({
      //     id: "ProLicenseStatus",
      //     header: () => <div className="th-text ">Activate License</div>,
      //     cell: (info) => {
      //       const company = info.row.original;
      //       const isActive = company?.LicenseActive;
      //       const buttonClass = isActive
      //         ? "license-btn license-deactivate"
      //         : "license-btn license-activate";

      //       return (
      //         <div className="d-flex justify-content-start text-center">
      //           <button
      //             className={`text-center ${buttonClass}`}
      //             onClick={() => handleProButton(company._id, !isActive)}
      //           >
      //             {isActive ? "Deactivate License" : "Activate License"}
      //           </button>
      //         </div>
      //       );
      //     },
      //   })
      // ]),

      // columnHelper.accessor("LicenseActive", {
      //   id: "LicenseActive",
      //   header: () => <div className="th-text">License Status</div>,
      //   cell: (info) => (
      //     <div className="text-sm ">
      //       {info.getValue() ? (
      //         <span className="status-badge badge-active">Active</span>
      //       ) : (
      //         <span className="status-badge badge-inactive">Inactive</span>
      //       )}
      //     </div>
      //   ),
      //   enableGlobalFilter: true,
      // }),

      columnHelper.accessor("status", {
        header: () => <div className="th-text">Company Status</div>,
        cell: (info) => {
          const value = info.getValue();
          const isActive = value === "Active";
          const className = isActive
            ? "status-radio status-radio-active"
            : "status-radio status-radio-inactive";

          return (
            <div className="">
              <input type="radio" className={className} checked readOnly />
            </div>
          );
        },
        enableGlobalFilter: false,
      }),

      columnHelper.display({
        id: "actions",
        header: () => <div className="th-text ">Actions</div>,
        cell: (info) => {
          const company = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              {canView && (
              <ViewIconButton
                to={`${baseRoute}/brokerageFirm/${company._id}`}
                className="btn action-btn fs-2 text-center"
                title="View"
              />
              )}
              {canEdit && (
              <EditIconButton
                to={`${baseRoute}/brokerageFirm/${company._id}/edit`}
                className="icon-square"
                title="Edit"
              />
              )}
              {canDelete && (
              <DeleteIconButton
                className="icon-square danger"
                title="Delete"
                onClick={() => handleDeleteClick(company)}
                disabled={deleting === company._id}
              />
              )}
            </div>
          );
        },
      })

    ];
  }, [deleting, handleProButton, pagination, isSignageTeammember, brokerageImageUrlMap, canView, canEdit, canDelete]);


  useEffect(() => {
    const rows: ExportRow[] = filteredFirms.map(firm => ({
      "Company Name": firm?.firmDetails?.companyName,
      "First Name": firm?.firstName || '',
      "Last Name": firm?.lastName || '',
      "Phone": firm?.firmDetails?.address?.phone || '',
      "Email": firm?.email || '',
      "License Status": firm?.LicenseActive ? 'Active' : 'Inactive',
      "Company Status": firm.status,
    }));

    setExportRows(rows);
  }, [filteredFirms]);

  const table = useReactTable({
    data: brokerageFirms,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;
  const showTableLoader = loading;

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
         <div className="d-flex  gap-2">
                                        <h3 className="m-0 fw-bold">
                                            Brokerage Firms
                                        </h3>
                                        {/* Help Icon */}
                                        <button
                                            onClick={() => setOpenHelp(true)}
                                            className="d-inline-flex align-items-center justify-content-center p-0 border-0 bg-transparent"
                                            style={{ cursor: "pointer" }}
                                        >
                                            <span
                                                className="d-inline-flex align-items-center justify-content-center"
                                                style={{
                                                    width: 26,
                                                    height: 26,
                                                    borderRadius: "50%",
                                                    background: "#eef4ff",
                                                }}
                                            >
                                                <i
                                                    className="bi bi-question-circle-fill"
                                                    style={{ fontSize: 18, color: "#155dfc" }}
                                                ></i>
                                            </span>
                                        </button>
                                        <BrokerageFirmHelpModal
                                            open={openHelp}
                                            onClose={() => setOpenHelp(false)}
                                        />
                                    </div>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/brokerageFirm`} className="btn btn-primary  table-addbutton">
            + Add Brokerage Firm
          </Link>
          )}
        </div>
      </div>
      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <i className="fs-24 ri-search-line text-primary" />
            <span className=".po-title-search fs-19 fw-normal ms-2">Search</span>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Brokerage Firm,Email"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
              />
            </div>
            <ExportAsExcel
              data={exportRows}
              headers={[
                "Company Name",
                "First Name",
                "Last Name",
                "Phone",
                "Email",
                "License Status",
                "Status",
              ]}
              fileName="BrokerageFirms"
            >
              {(props) => (
                <button {...props} className="export-button">
                  <i className="ri-file-excel-2-line me-2" />
                  Export to Excel
                </button>
              )}
            </ExportAsExcel>
          </div>
        </div>
      </div>
      {/* <h2 className="d-flex align-items-center justify-content-center">Brokerage Firms</h2> */}
      {/* <div className="row me-3 justify-content-end mb-4">

        <div className="col-md-6 d-flex gap-2 justify-content-end "> */}
      {/* <div className="input-group" style={{ width: "230px", height: "40px", marginRight: "30px" }}>
                <span className="input-group-text bg-white border-end-0 p-2 left-radius">
                  <i className="ri-search-line fs-15"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0 right-radius"
                  placeholder="Search by Name, Email, first name"
                  value={searchInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      setGlobalFilter(searchInput);
                    }
                  }}
                  style={{ borderColor: "#cccccc" }}
                />
              </div> */}



      {/* <Link to="/signage/addBrokerageFirm" className="btn btn-primary">
                Add Brokerage Firm
              </Link> */}
      {/* <input
                type="text"
                className="form-control"
                placeholder="Search by Name, Email, first name"
                value={globalFilter ?? ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
              /> */}

      {/* </div>
      </div> */}

      {/* Table  */}

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            orders
          </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {showTableLoader ? (
                <DesktopTableLoader table={table} message="Loading brokerage firms…" variant="dots" />
              ) : (
                <DesktopTableParts table={table} />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          {showTableLoader ? (
            <MobileCardsLoader message="Loading brokerage firms…" variant="dots" />
          ) : (
            <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
          )}
        </div>

      </div>

      <DataTableStatus
        loading={showTableLoader}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
      {/* <div className="custom-table-wrapper">
        <DataTable table={table} />
      </div>

      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="border-bottom pb-3 mb-4 page-container d-block">
          <PaginationControls table={table} />
        </div>
      )} */}
      <DeleteConfirmModal
        open={openDelete}
        dynamicName={deleteTargetName || "Firm"}
        onCancel={() => {
          setOpenDelete(false);
          setDeleteTargetId(null);
          setDeleteTargetName("");
        }}
        onDelete={confirmDeleteFirm}
      />
    </div>
  );
};

export default SignageBrokerageFirms;
